// components/ProfessionalProfileConfig.tsx - VERSIÓN ACTUALIZADA CON ENV
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, XCircle, Phone, Building2, AlertTriangle, User, Briefcase, Camera, ShieldCheck, Users, GraduationCap, MapPin, Loader2 } from 'lucide-react';
import ProfessionalPhotoManager from './ProfessionalPhotoManager';
import TwoFactorSetup from './TwoFactorSetup';
import AssistantsManager from './AssistantsManager';
import { PortadaMarca } from './MarcaUI';

interface ProfessionalData {
  id: number;
  nombre: string;
  apellido: string;
  email: string;
  telefono: string;
  business_name: string;
  business_description: string;
  business_address: string;
  picture: string;
  cover_photo: string;
  fecha_nacimiento: string;
  genero: string;
  direccion: string;
  especialidad: string;
  matricula: string;
  matricula_jurisdiccion: string;
  professional_type: string;
  dni?: string;
  refeps_id?: string;
  matricula_validada_msal?: boolean;
  matricula_validada_at?: string | null;
}

interface FormData {
  nombre: string;
  apellido: string;
  telefono: string;
  profesion: string;
  especialidad: string;
  matricula: string;
  matricula_jurisdiccion: string;
  business_name: string;
  business_description: string;
  business_address: string;
  dni: string;
  genero: string;
}

const ProfessionalProfileConfig: React.FC = () => {
  const navigate = useNavigate();
  
  // Obtener la URL base del backend desde las variables de entorno
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
  
  const [professionalData, setProfessionalData] = useState<ProfessionalData | null>(null);
  const [formData, setFormData] = useState<FormData>({
    nombre: '',
    apellido: '',
    telefono: '',
    profesion: '',
    especialidad: '',
    matricula: '',
    matricula_jurisdiccion: 'Argentina',
    business_name: '',
    business_description: '',
    business_address: '',
    dni: '',
    genero: ''
  });
  
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  // ?tab=security abre directo esa solapa. Sin esto, la sugerencia de 2FA de
  // Recetas dejaba al profesional en "Datos básicos" buscando dónde seguir.
  const [parametrosURL] = useSearchParams();
  const solapaPedida = parametrosURL.get('tab');
  const SOLAPAS = ['basic', 'business', 'photos', 'security', 'assistants'] as const;
  type Solapa = typeof SOLAPAS[number];
  const [currentTab, setCurrentTab] = useState<Solapa>(
    SOLAPAS.includes(solapaPedida as Solapa) ? (solapaPedida as Solapa) : 'basic'
  );

  // Modal de validación de matrícula contra REFEPS.
  // Se muestra al guardar cuando hay revalidación: primero "consultando",
  // después el resultado con los datos que devolvió el Ministerio.
  const [validationModal, setValidationModal] = useState<{
    open: boolean;
    status: 'loading' | 'success' | 'warning' | 'error' | 'info';
    snapshot?: any;
    message?: string;
  }>({ open: false, status: 'loading' });

  // Estados para 2FA
  const [twoFactorStatus, setTwoFactorStatus] = useState({
    enabled: false,
    // `required` quedó siempre en false: el 2FA dejó de ser obligatorio para
    // emitir recetas. Se conserva porque el backend lo sigue mandando.
    required: false,
    recomendado: false,
    canEnable: false,
    backupCodesRemaining: 0
  });
  const [show2FASetup, setShow2FASetup] = useState(false);
  const [isLoading2FA, setIsLoading2FA] = useState(true);
  const [showDisable2FA, setShowDisable2FA] = useState(false);
  const [disablePassword, setDisablePassword] = useState('');
  const [disableCode, setDisableCode] = useState('');
  const [disableError, setDisableError] = useState('');
  const [disableLoading, setDisableLoading] = useState(false);

  useEffect(() => {
    loadProfessionalData();
    load2FAStatus();
  }, []);

  const loadProfessionalData = () => {
    try {
      const userData = localStorage.getItem('user');
      if (!userData) {
        navigate('/login');
        return;
      }

      const user = JSON.parse(userData);
      if (!user.es_profesional) {
        navigate('/dashboard/paciente');
        return;
      }

      setProfessionalData(user);
      setFormData({
        nombre: user.nombre || '',
        apellido: user.apellido || '',
        telefono: user.telefono || '',
        profesion: user.profesion || (user.professional_type === 'salud' ? 'Médico' : ''),
        especialidad: user.especialidad || '',
        matricula: user.matricula || '',
        matricula_jurisdiccion: user.matricula_jurisdiccion || 'Argentina',
        business_name: user.business_name || '',
        business_description: user.business_description || '',
        business_address: user.business_address || '',
        dni: user.dni || '',
        genero: user.genero || ''
      });
      
    } catch (error) {
      console.error('Error cargando datos:', error);
      setMessage({ type: 'error', text: 'Error cargando los datos del perfil' });
    } finally {
      setIsLoading(false);
    }
  };

  const load2FAStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/auth/2fa/status`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setTwoFactorStatus(data);
      }
    } catch (error) {
      console.error('Error cargando estado 2FA:', error);
    } finally {
      setIsLoading2FA(false);
    }
  };

  const handleDisable2FA = async () => {
    if (!disablePassword || disableCode.length !== 6) {
      setDisableError('Ingresá tu contraseña y el código de 6 dígitos');
      return;
    }
    setDisableLoading(true);
    setDisableError('');
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/auth/2fa/disable`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ token: disableCode, password: disablePassword })
      });
      const data = await response.json();
      if (response.ok && data.success) {
        setShowDisable2FA(false);
        setDisablePassword('');
        setDisableCode('');
        setTwoFactorStatus(prev => ({ ...prev, enabled: false, backupCodesRemaining: 0 }));
        alert('Verificación en dos pasos desactivada correctamente');
      } else {
        setDisableError(data.error || 'Error al desactivar');
      }
    } catch (err) {
      setDisableError('Error de conexión');
    } finally {
      setDisableLoading(false);
    }
  };

  const handleInputChange = (field: keyof FormData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  /**
   * Estado reactivo del badge de validación de matrícula. Se recalcula en cada
   * render comparando lo que el profesional está editando (formData) contra lo
   * último guardado y validado (professionalData):
   *  - 'empty'        → falta cargar matrícula
   *  - 'pending'      → hay cambios sin guardar en matrícula/DNI/género
   *  - 'validated'    → guardado y validado por REFEPS (badge verde)
   *  - 'not_validated'→ guardado pero sin validación REFEPS (badge amarillo)
   */
  // OJO: quedó inerte. Comparaba la matrícula que se estaba editando contra la
  // guardada, pero esta pantalla ya no tiene ese campo: se administra en
  // /professional/habilitar-recetas. Como formData.matricula ya nunca difiere
  // de la guardada, nunca devuelve 'pending' y el modal de validación de más
  // abajo no se abre. Se deja porque no molesta; si se limpia, hay que sacar
  // también willValidateRefeps y validationModal.
  const getMatriculaState = (): 'empty' | 'pending' | 'validated' | 'not_validated' => {
    const m = (formData.matricula || '').trim();
    const d = (formData.dni || '').trim();
    const g = (formData.genero || '').trim();
    if (!m) return 'empty';
    const sameAsSaved =
      m === (professionalData?.matricula || '').trim() &&
      d === (professionalData?.dni || '').trim() &&
      g === (professionalData?.genero || '').trim();
    if (!sameAsSaved) return 'pending';
    if (professionalData?.matricula_validada_msal) return 'validated';
    return 'not_validated';
  };

  const handleSave = async () => {
    if (!professionalData) return;
    
    setIsSaving(true);
    setMessage({ type: '', text: '' });

    try {
      // Validaciones básicas
      if (!formData.nombre.trim() || !formData.apellido.trim()) {
        setMessage({ type: 'error', text: 'Nombre y apellido son obligatorios' });
        return;
      }

      console.log('📝 Guardando perfil - SOLO campos de texto:', formData);
      console.log('🖼️ Imágenes actuales (NO se envían):', {
        picture: professionalData.picture,
        cover_photo: professionalData.cover_photo
      });

      // 🔑 CLAVE: Enviar SOLO los campos que realmente tenemos (filtrar vacíos)
      const dataToSend = {
        nombre: formData.nombre,
        apellido: formData.apellido,
        telefono: formData.telefono,
        profesion: formData.profesion,
        especialidad: formData.especialidad,
        business_name: formData.business_name,
        business_description: formData.business_description,
        business_address: formData.business_address,
      };
      // matricula, matricula_jurisdiccion, dni y genero NO se mandan desde
      // acá: los administra /professional/habilitar-recetas, que los valida
      // contra REFEPS. Si esta pantalla los siguiera enviando, guardar el
      // perfil pisaría con datos viejos lo que aquella acaba de validar.

      console.log('📤 Datos a enviar:', dataToSend);

      // ¿Esta guardada va a disparar una revalidación contra REFEPS?
      // Pasa cuando es profesional de salud, hay cambios en matrícula/DNI/género
      // y los 3 datos están completos. Si es así, abrimos el modal "consultando".
      const willValidateRefeps =
        professionalData.professional_type === 'salud' &&
        getMatriculaState() === 'pending' &&
        !!formData.dni.trim() &&
        !!formData.matricula.trim() &&
        !!formData.genero.trim();

      if (willValidateRefeps) {
        setValidationModal({ open: true, status: 'loading' });
      }

      // Obtener token JWT del localStorage
      const token = localStorage.getItem('token');
      console.log('🔑 Token JWT:', token ? 'Presente' : 'NO ENCONTRADO');

      const response = await fetch(`${API_BASE_URL}/api/professionals/${professionalData.id}/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(dataToSend),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Error al actualizar el perfil');
      }

      const result = await response.json();
      console.log('✅ Respuesta del servidor:', result);
      
      // Actualizar localStorage con la respuesta del servidor
      if (result.userForLocalStorage) {
        console.log('🔄 Actualizando localStorage con datos preservados');
        localStorage.setItem('user', JSON.stringify(result.userForLocalStorage));
        setProfessionalData(result.userForLocalStorage);
        
        // Disparar evento para que otros componentes se enteren del cambio
        window.dispatchEvent(new Event('userDataUpdated'));
      } else {
        // Fallback: actualizar solo campos de texto
        const updatedUser = { ...professionalData, ...formData };
        localStorage.setItem('user', JSON.stringify(updatedUser));
        setProfessionalData(updatedUser);
      }
      
      setMessage({
        type: 'success',
        text: result.imagesPreserved
          ? 'Perfil actualizado exitosamente (imágenes preservadas)'
          : 'Perfil actualizado exitosamente'
      });

      console.log('✅ Estado de imágenes después de guardar:', result.imagesPreserved);

      // Actualizar el modal de validación con el resultado de REFEPS
      if (willValidateRefeps) {
        const mv = result.msalValidation;
        if (mv && mv.ok && mv.valid) {
          setValidationModal({ open: true, status: 'success', snapshot: mv.snapshot });
        } else if (mv && mv.ok && !mv.valid) {
          setValidationModal({
            open: true,
            status: 'warning',
            snapshot: mv.snapshot,
            message: 'REFEPS no reporta tu matrícula como habilitada. Revisá que el número de matrícula, el DNI y el género sean correctos.',
          });
        } else if (mv && mv.transient) {
          setValidationModal({
            open: true,
            status: 'error',
            message: 'El servicio del Ministerio de Salud no respondió en este momento. Tu perfil se guardó igual; podés reintentar la validación más tarde volviendo a guardar.',
          });
        } else {
          setValidationModal({
            open: true,
            status: 'info',
            message: 'Tu perfil se guardó correctamente.',
          });
        }
      }

    } catch (error) {
      console.error('❌ Error actualizando perfil:', error);
      setMessage({ type: 'error', text: error instanceof Error ? error.message : 'Error al actualizar el perfil' });
      // Si el modal de validación estaba abierto en "loading", pasarlo a error
      setValidationModal(prev =>
        prev.open && prev.status === 'loading'
          ? { open: true, status: 'error', message: 'No se pudo completar el guardado. Revisá tu conexión e intentá de nuevo.' }
          : prev
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handlePhotoUpdate = async (type: 'profile' | 'cover', photoUrl: string) => {
    if (!professionalData) return;
    
    console.log(`📸 Actualizando ${type === 'profile' ? 'foto de perfil' : 'foto de portada'}:`, photoUrl);
    
    try {
      // Actualizar usando el endpoint específico para imágenes
      // El endpoint pasa a pedir token y a verificar que el perfil sea el tuyo.
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/professionals/${professionalData.id}/images`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify({
          [type === 'profile' ? 'picture' : 'cover_photo']: photoUrl
        }),
      });

      if (!response.ok) {
        throw new Error('Error al actualizar la imagen');
      }

      const result = await response.json();
      console.log('✅ Imagen actualizada en BD:', result);

      // Actualizar estado local y localStorage
      const updatedData = {
        ...professionalData,
        [type === 'profile' ? 'picture' : 'cover_photo']: photoUrl
      };
      
      setProfessionalData(updatedData);
      localStorage.setItem('user', JSON.stringify(updatedData));
      
      // Disparar evento para actualizar otros componentes
      window.dispatchEvent(new Event('userDataUpdated'));
      
      setMessage({ 
        type: 'success', 
        text: `${type === 'profile' ? 'Foto de perfil' : 'Foto de portada'} actualizada exitosamente`
      });

    } catch (error) {
      console.error(`❌ Error actualizando ${type}:`, error);
      setMessage({ 
        type: 'error', 
        text: `Error al actualizar la ${type === 'profile' ? 'foto de perfil' : 'foto de portada'}`
      });
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <div className="animate-spin h-12 w-12 border-4 border-blue-600 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-gray-600">Cargando configuración...</p>
        </div>
      </div>
    );
  }

  if (!professionalData) {
    return (
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="max-w-2xl mx-auto px-4 text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Error de sesión</h2>
          <p className="text-gray-600 mb-6">No se pudieron cargar los datos del profesional</p>
          <button
            onClick={() => navigate('/login')}
            className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Iniciar sesión
          </button>
        </div>
      </div>
    );
  }

  // La solapa de Seguridad solo existe para profesionales de salud.
  //
  // El 2FA está en la app por las recetas, y quien no receta no puede
  // activarlo: el backend rechaza el alta con un 403. Hasta ahora igual se
  // les mostraba el botón, así que una manicura entraba a Seguridad, tocaba
  // "Activar 2FA" y recibía un error rojo. Un botón que solo servía para
  // decir que no.
  const esProfesionalSalud = professionalData.professional_type === 'salud';

  const solapasVisibles = [
    { id: 'basic', label: 'Básico', icon: <User className="w-4 h-4" /> },
    { id: 'business', label: 'Negocio', icon: <Briefcase className="w-4 h-4" /> },
    { id: 'photos', label: 'Fotos', icon: <Camera className="w-4 h-4" /> },
    ...(esProfesionalSalud
      ? [{ id: 'security', label: 'Seguridad', icon: <ShieldCheck className="w-4 h-4" /> }]
      : []),
    { id: 'assistants', label: 'Asistentes', icon: <Users className="w-4 h-4" /> },
  ];

  // Si alguien llega con ?tab=security sin ser de salud, cae en Básico en
  // lugar de quedarse mirando una pantalla en blanco.
  const solapaActiva = solapasVisibles.some((s) => s.id === currentTab) ? currentTab : 'basic';

  return (
    <div className={`marca marca--${esProfesionalSalud ? 'salud' : 'belleza'} min-h-screen bg-slate-50 text-left`}>
      {/* Lavada de cara oct-2026: el diseño del asistente, Horarios, Servicios y
          Pagos (MarcaUI). Fotos, verificación en dos pasos y asistentes son
          componentes aparte y conservan el suyo. */}
      <PortadaMarca
        titulo="Mi perfil"
        bajada="Tus datos, tu negocio y cómo te ven en tu página."
        onVolver={() => navigate('/dashboard/profesional')}
      >
        <div className="flex flex-wrap gap-2 text-xs font-semibold">
          <span className="rounded-full bg-white/20 px-3 py-1">
            {`${professionalData.nombre || ''} ${professionalData.apellido || ''}`.trim()}
          </span>
          {professionalData.especialidad && (
            <span className="rounded-full bg-white/20 px-3 py-1">{professionalData.especialidad}</span>
          )}
        </div>
      </PortadaMarca>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 -mt-6 relative pb-32 space-y-4">
        {/* Solapas: en el celular se deslizan de costado (son hasta cinco) */}
        <div className="rounded-2xl bg-white border border-gray-200 p-1 shadow-sm overflow-x-auto" role="tablist">
          <div className="flex gap-1 min-w-max">
            {solapasVisibles.map(tab => (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={solapaActiva === tab.id}
                onClick={() => setCurrentTab(tab.id as any)}
                className={`flex-1 flex items-center justify-center gap-1.5 rounded-xl py-2.5 px-3.5 text-sm font-semibold whitespace-nowrap transition-colors ${
                  solapaActiva === tab.id ? 'marca-tarjeta border marca-acento' : 'text-gray-500 hover:text-gray-700 border border-transparent'
                }`}
              >
                {tab.icon}
                <span>{tab.label}</span>
              </button>
            ))}
          </div>
        </div>

        {message.text && (
          <div className={`p-4 rounded-2xl ${
            message.type === 'success' 
              ? 'bg-green-50 border border-green-200 text-green-800' 
              : 'bg-red-50 border border-red-200 text-red-800'
          }`}>
            <div className="flex items-start">
              <span className="mr-2">
                {message.type === 'success' ? <CheckCircle className="w-5 h-5 text-green-600" /> : <XCircle className="w-5 h-5 text-red-600" />}
              </span>
              <div>
                {message.text}
                {message.type === 'success' && (
                  <p className="text-sm mt-1 opacity-75">
                    Los cambios se han guardado y sincronizado correctamente.
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Tab Content */}
        {solapaActiva === 'basic' && (
          <div className="bg-white rounded-3xl border border-gray-200 shadow-sm p-4 sm:p-6">
            <h2 className="text-base font-bold text-gray-900 mb-4">Información Básica</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nombre *
                </label>
                <input
                  type="text"
                  value={formData.nombre}
                  onChange={(e) => handleInputChange('nombre', e.target.value)}
                  className="marca-campo w-full px-4 py-2.5 border border-gray-300 rounded-xl text-base sm:text-sm bg-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Apellido *
                </label>
                <input
                  type="text"
                  value={formData.apellido}
                  onChange={(e) => handleInputChange('apellido', e.target.value)}
                  className="marca-campo w-full px-4 py-2.5 border border-gray-300 rounded-xl text-base sm:text-sm bg-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <span className="flex items-center gap-1"><Phone className="w-4 h-4" /> Telefono / WhatsApp</span>
                </label>
                <input
                  type="tel"
                  value={formData.telefono}
                  onChange={(e) => handleInputChange('telefono', e.target.value)}
                  placeholder="Ej: 3794080538"
                  className="marca-campo w-full px-4 py-2.5 border border-gray-300 rounded-xl text-base sm:text-sm bg-white"
                />
                <p className="text-sm text-gray-500 mt-1">
                  Este número se usará para notificaciones y aparecerá en los recordatorios
                </p>
              </div>

              {/* Profesión - Solo profesionales de salud (requerido por Decreto 63/2024 en recetas) */}
              {professionalData?.professional_type === 'salud' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
                    <GraduationCap className="w-4 h-4 text-gray-600" /> Profesión *
                  </label>
                  <select
                    value={formData.profesion}
                    onChange={(e) => handleInputChange('profesion', e.target.value)}
                    className="marca-campo w-full px-4 py-2.5 border border-gray-300 rounded-xl text-base sm:text-sm bg-white bg-white"
                  >
                    <option value="">Seleccionar profesión</option>
                    <option value="Médico">Médico</option>
                    <option value="Odontólogo">Odontólogo</option>
                    <option value="Bioquímico">Bioquímico</option>
                    <option value="Farmacéutico">Farmacéutico</option>
                    <option value="Enfermero">Enfermero/a</option>
                    <option value="Kinesiólogo">Kinesiólogo</option>
                    <option value="Psicólogo">Psicólogo</option>
                    <option value="Nutricionista">Nutricionista</option>
                    <option value="Fonoaudiólogo">Fonoaudiólogo</option>
                    <option value="Obstétrico">Obstétrico/a</option>
                    <option value="Veterinario">Veterinario</option>
                    <option value="Otra">Otra</option>
                  </select>
                  <p className="text-sm text-gray-500 mt-1">
                    Profesión que figura en tu matrícula (requerido por Decreto 63/2024 para emitir recetas).
                  </p>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
                  <GraduationCap className="w-4 h-4 text-gray-600" /> Especialidad
                </label>
                <input
                  type="text"
                  value={formData.especialidad}
                  onChange={(e) => handleInputChange('especialidad', e.target.value)}
                  placeholder="Ej: Dermatología, Estética Facial, etc."
                  className="marca-campo w-full px-4 py-2.5 border border-gray-300 rounded-xl text-base sm:text-sm bg-white"
                />
                <p className="text-sm text-gray-500 mt-1">
                  Tu especialidad principal que se mostrará en tu perfil
                </p>
              </div>

              {/* ---------------------------------------------------------
                  Matrícula: solo lectura acá.

                  Antes esta pantalla pedía DNI, género, número de matrícula y
                  jurisdicción, y con eso disparaba la validación contra
                  REFEPS. Ahora eso vive en /professional/habilitar-recetas,
                  que busca por DNI y deja elegir la matrícula de las que
                  devuelve el registro — el número no se tipea, porque REFEPS
                  no se puede consultar por él.

                  Igual de importante: esta pantalla ya NO manda matricula,
                  matricula_jurisdiccion, dni ni genero al guardar. Si los
                  siguiera mandando, guardar el perfil pisaría con datos
                  viejos lo que acaba de validar la otra pantalla.
                  --------------------------------------------------------- */}
              {professionalData?.professional_type === 'salud' && (
                <div className="border border-gray-200 rounded-xl p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-gray-800">Matrícula profesional</p>

                      {professionalData?.matricula ? (
                        <>
                          <p className="text-lg font-bold text-gray-900 mt-1">
                            {professionalData.matricula}
                          </p>
                          {professionalData?.matricula_validada_msal ? (
                            <p className="text-sm text-green-700 flex items-center gap-1.5 mt-1">
                              <CheckCircle className="w-4 h-4 shrink-0" />
                              Validada contra REFEPS
                            </p>
                          ) : (
                            <p className="text-sm text-amber-700 mt-1">
                              Cargada, pero todavía sin validar
                            </p>
                          )}
                        </>
                      ) : (
                        <p className="text-sm text-gray-600 mt-1">
                          Todavía no cargaste tu matrícula. Hace falta para emitir recetas
                          electrónicas.
                        </p>
                      )}
                    </div>

                    <a
                      href="/professional/habilitar-recetas"
                      className="marca-boton shrink-0 px-4 py-2 rounded-xl text-sm font-semibold"
                    >
                      {professionalData?.matricula ? 'Cambiar' : 'Cargar'}
                    </a>
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end mt-6">
              <button
                onClick={handleSave}
                disabled={isSaving}
                className="marca-boton px-6 py-3 rounded-xl font-semibold disabled:opacity-50 flex items-center justify-center space-x-2 w-full sm:w-auto"
              >
                {isSaving ? (
                  <>
                    <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                    <span>Guardando...</span>
                  </>
                ) : (
                  <span>Guardar cambios</span>
                )}
              </button>
            </div>
          </div>
        )}

        {solapaActiva === 'business' && (
          <div className="bg-white rounded-3xl border border-gray-200 shadow-sm p-4 sm:p-6">
            <h2 className="text-base font-bold text-gray-900 mb-4">Información Comercial</h2>
            
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nombre del negocio
                </label>
                <input
                  type="text"
                  value={formData.business_name}
                  onChange={(e) => handleInputChange('business_name', e.target.value)}
                  placeholder="Ej: Clínica Dental Dr. García"
                  className="marca-campo w-full px-4 py-2.5 border border-gray-300 rounded-xl text-base sm:text-sm bg-white"
                />
                <p className="text-sm text-gray-500 mt-1">
                  Este nombre aparecerá en tu perfil público
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Descripción del negocio
                </label>
                <textarea
                  rows={4}
                  value={formData.business_description}
                  onChange={(e) => handleInputChange('business_description', e.target.value)}
                  placeholder="Describe tus servicios, experiencia, especialidades..."
                  className="marca-campo w-full px-4 py-2.5 border border-gray-300 rounded-xl text-base sm:text-sm bg-white"
                />
                <p className="text-sm text-gray-500 mt-1">
                  Esta descripción ayudará a los pacientes a conocerte mejor
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Dirección del consultorio/negocio
                </label>
                <input
                  type="text"
                  value={formData.business_address}
                  onChange={(e) => handleInputChange('business_address', e.target.value)}
                  placeholder="Dirección donde atiendes a tus pacientes"
                  className="marca-campo w-full px-4 py-2.5 border border-gray-300 rounded-xl text-base sm:text-sm bg-white"
                />
              </div>
            </div>

            <div className="flex justify-end mt-6">
              <button
                onClick={handleSave}
                disabled={isSaving}
                className="marca-boton px-6 py-3 rounded-xl font-semibold disabled:opacity-50 flex items-center justify-center space-x-2 w-full sm:w-auto"
              >
                {isSaving ? (
                  <>
                    <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                    <span>Guardando...</span>
                  </>
                ) : (
                  <span>Guardar cambios</span>
                )}
              </button>
            </div>
          </div>
        )}

        {solapaActiva === 'photos' && professionalData && (
          <ProfessionalPhotoManager
            currentProfilePhoto={professionalData.picture}
            currentCoverPhoto={professionalData.cover_photo}
            onProfilePhotoUpdate={(url) => handlePhotoUpdate('profile', url)}
            onCoverPhotoUpdate={(url) => handlePhotoUpdate('cover', url)}
            professionalId={professionalData.id}
          />
        )}

        {solapaActiva === 'security' && (
          <div className="bg-white rounded-3xl border border-gray-200 shadow-sm p-4 sm:p-6">
            <h2 className="text-base font-bold text-gray-900 mb-4">Seguridad de la Cuenta</h2>
            
            {/* Sección 2FA */}
            <div className="border border-gray-200 rounded-xl p-6">
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-4">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                    twoFactorStatus.enabled 
                      ? 'bg-green-100 text-green-600' 
                      : 'bg-gray-100 text-gray-400'
                  }`}>
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      Verificación en dos pasos (2FA)
                    </h3>
                    <p className="text-gray-600 mt-1">
                      {twoFactorStatus.enabled 
                        ? 'Tu cuenta está protegida con verificación en dos pasos'
                        : 'Agrega una capa extra de seguridad a tu cuenta'
                      }
                    </p>
                    
                    {twoFactorStatus.enabled && (
                      <div className="mt-3 flex items-center space-x-4">
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                          ✓ Activo
                        </span>
                        <span className="text-sm text-gray-500">
                          {twoFactorStatus.backupCodesRemaining} códigos de respaldo restantes
                        </span>
                      </div>
                    )}
                    
                    {!twoFactorStatus.enabled && twoFactorStatus.recomendado && (
                      <div className="mt-3 p-3 bg-[#0FA3B1]/8 border border-[#0FA3B1]/30 rounded-lg">
                        <p className="text-sm text-gray-800">
                          <ShieldCheck className="w-4 h-4 inline mr-1 text-[#0B7C87]" />
                          <strong className="text-[#0B7C87]">Recomendado:</strong> como firmás recetas
                          con tu matrícula, este refuerzo protege tu cuenta aunque alguien
                          averigüe tu contraseña. No es obligatorio.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
                
                <div>
                  {isLoading2FA ? (
                    <div className="animate-spin h-6 w-6 border-2 border-blue-600 border-t-transparent rounded-full"></div>
                  ) : twoFactorStatus.enabled ? (
                    <button
                      onClick={() => { setShowDisable2FA(true); setDisableError(''); setDisablePassword(''); setDisableCode(''); }}
                      className="px-4 py-2 text-red-600 border border-red-200 rounded-lg hover:bg-red-50 transition-colors"
                    >
                      Desactivar
                    </button>
                  ) : (
                    <button
                      onClick={() => setShow2FASetup(true)}
                      className="marca-boton px-4 py-2 rounded-xl font-semibold shrink-0"
                    >
                      Activar 2FA
                    </button>
                  )}
                </div>
              </div>
            </div>
            
            {/* Información adicional */}
            {/* text-left a proposito: la app hereda text-align:center de la
                regla .App de Create React App y la lista queda centrada. */}
            <div className="mt-6 p-4 bg-blue-50 rounded-xl text-left">
              <h4 className="font-medium text-blue-900 mb-2">¿Conviene activarla?</h4>
              <ul className="text-sm text-blue-900 space-y-1">
                <li>• Protege tu cuenta aunque alguien conozca tu contraseña</li>
                <li>• Es opcional: las recetas electrónicas funcionan igual sin esto</li>
                <li>• Hace falta una app de códigos en el celular (Google Authenticator, Authy)</li>
                <li>• Al iniciar sesión te pide ese código, además de la contraseña</li>
              </ul>
            </div>
          </div>
        )}

        {/* Pestaña Asistentes */}
        {solapaActiva === 'assistants' && (
          <AssistantsManager professionalId={professionalData?.id} />
        )}

        {/* Modal de configuración 2FA */}
        <TwoFactorSetup
          isOpen={show2FASetup}
          onClose={() => setShow2FASetup(false)}
          onSuccess={() => {
            load2FAStatus();
            setMessage({ type: 'success', text: '¡Verificación en dos pasos activada correctamente!' });
          }}
        />

        {/* Información de debug (solo en desarrollo) */}
        {process.env.REACT_APP_DEBUG_MODE === 'true' && (
          <div className="mt-8 bg-gray-100 rounded-lg p-4">
            <h3 className="font-medium text-gray-900 mb-2">Debug Info:</h3>
            <div className="text-sm text-gray-600 space-y-1">
              <p>API Base URL: {API_BASE_URL}</p>
              <p>Imágenes actuales:</p>
              <p>- Perfil: {professionalData?.picture ? 'Presente' : 'No definida'}</p>
              <p>- Portada: {professionalData?.cover_photo ? 'Presente' : 'No definida'}</p>
              <p>• ID: {professionalData?.id}</p>
              <p>• Environment: {process.env.REACT_APP_NODE_ENV}</p>
            </div>
          </div>
        )}
      </div>

      {/* Modal desactivar 2FA */}
      {showDisable2FA && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100] p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Desactivar 2FA</h3>
                <p className="text-sm text-gray-500">Ingresá tu contraseña y código actual</p>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Contraseña</label>
                <input
                  type="password"
                  value={disablePassword}
                  onChange={(e) => setDisablePassword(e.target.value)}
                  placeholder="Tu contraseña actual"
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Código de verificación</label>
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={disableCode}
                  onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, ''))}
                  placeholder="000000"
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-center text-xl font-mono tracking-widest"
                />
              </div>

              {disableError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-red-600 text-sm text-center">{disableError}</p>
                </div>
              )}
            </div>

            <div className="mt-6 flex gap-3">
              <button
                onClick={() => setShowDisable2FA(false)}
                disabled={disableLoading}
                className="flex-1 px-4 py-2.5 text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleDisable2FA}
                disabled={disableLoading || !disablePassword || disableCode.length !== 6}
                className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {disableLoading && (
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                )}
                Desactivar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===== Modal de validación de matrícula contra REFEPS ===== */}
      {validationModal.open && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">

            {/* Estado: consultando */}
            {validationModal.status === 'loading' && (
              <div className="p-8 text-center">
                <Loader2 className="w-12 h-12 text-blue-500 animate-spin mx-auto mb-4" />
                <h3 className="text-lg font-bold text-gray-900">Validando matrícula...</h3>
                <p className="text-sm text-gray-500 mt-1">
                  Consultando REFEPS — Ministerio de Salud de la Nación
                </p>
              </div>
            )}

            {/* Estado: validada OK */}
            {validationModal.status === 'success' && (
              <div>
                <div className="bg-green-50 border-b border-green-100 p-6 text-center">
                  <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <CheckCircle className="w-8 h-8 text-green-600" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900">Matrícula validada</h3>
                  <p className="text-sm text-gray-500 mt-0.5">
                    Datos confirmados por REFEPS — Ministerio de Salud de la Nación
                  </p>
                </div>
                <div className="p-6 space-y-3">
                  {validationModal.snapshot?.nombreCompleto && (
                    <div>
                      <p className="text-xs text-gray-400 uppercase tracking-wide">Profesional</p>
                      <p className="font-semibold text-gray-900">{validationModal.snapshot.nombreCompleto}</p>
                    </div>
                  )}
                  {validationModal.snapshot?.dni && (
                    <div>
                      <p className="text-xs text-gray-400 uppercase tracking-wide">DNI</p>
                      <p className="text-gray-800">{validationModal.snapshot.dni}</p>
                    </div>
                  )}
                  {Array.isArray(validationModal.snapshot?.matriculas) && validationModal.snapshot.matriculas.length > 0 && (
                    <div>
                      <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">Matrículas registradas</p>
                      <div className="space-y-2">
                        {validationModal.snapshot.matriculas.map((mat: any, i: number) => (
                          <div key={i} className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2">
                            <div>
                              <p className="font-medium text-gray-900 text-sm">
                                {mat.tipo || 'Matrícula'} N° {mat.numero}
                              </p>
                              {mat.issuer && (
                                <p className="text-xs text-gray-500">{mat.issuer}</p>
                              )}
                            </div>
                            {mat.habilitada ? (
                              <span className="text-xs font-semibold text-green-700 bg-green-100 px-2 py-0.5 rounded-full">
                                Habilitada
                              </span>
                            ) : (
                              <span className="text-xs font-semibold text-red-700 bg-red-100 px-2 py-0.5 rounded-full">
                                No habilitada
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                <div className="px-6 pb-6">
                  <button
                    onClick={() => setValidationModal({ open: false, status: 'loading' })}
                    className="w-full py-2.5 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-colors"
                  >
                    Entendido
                  </button>
                </div>
              </div>
            )}

            {/* Estado: matrícula no habilitada */}
            {validationModal.status === 'warning' && (
              <div>
                <div className="bg-yellow-50 border-b border-yellow-100 p-6 text-center">
                  <div className="w-14 h-14 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <AlertTriangle className="w-8 h-8 text-yellow-600" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900">Matrícula no habilitada</h3>
                </div>
                <div className="p-6">
                  <p className="text-sm text-gray-600">{validationModal.message}</p>
                  {validationModal.snapshot?.nombreCompleto && (
                    <p className="text-sm text-gray-500 mt-3">
                      REFEPS devolvió: <strong>{validationModal.snapshot.nombreCompleto}</strong>
                    </p>
                  )}
                </div>
                <div className="px-6 pb-6">
                  <button
                    onClick={() => setValidationModal({ open: false, status: 'loading' })}
                    className="w-full py-2.5 bg-yellow-600 text-white rounded-lg font-medium hover:bg-yellow-700 transition-colors"
                  >
                    Entendido
                  </button>
                </div>
              </div>
            )}

            {/* Estado: error / servicio caído */}
            {validationModal.status === 'error' && (
              <div>
                <div className="bg-red-50 border-b border-red-100 p-6 text-center">
                  <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <XCircle className="w-8 h-8 text-red-600" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900">No se pudo validar</h3>
                </div>
                <div className="p-6">
                  <p className="text-sm text-gray-600">{validationModal.message}</p>
                </div>
                <div className="px-6 pb-6">
                  <button
                    onClick={() => setValidationModal({ open: false, status: 'loading' })}
                    className="w-full py-2.5 bg-gray-700 text-white rounded-lg font-medium hover:bg-gray-800 transition-colors"
                  >
                    Cerrar
                  </button>
                </div>
              </div>
            )}

            {/* Estado: info */}
            {validationModal.status === 'info' && (
              <div>
                <div className="bg-blue-50 border-b border-blue-100 p-6 text-center">
                  <div className="w-14 h-14 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <CheckCircle className="w-8 h-8 text-blue-600" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900">Perfil guardado</h3>
                </div>
                <div className="p-6">
                  <p className="text-sm text-gray-600">{validationModal.message}</p>
                </div>
                <div className="px-6 pb-6">
                  <button
                    onClick={() => setValidationModal({ open: false, status: 'loading' })}
                    className="w-full py-2.5 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
                  >
                    Entendido
                  </button>
                </div>
              </div>
            )}

          </div>
        </div>
      )}
    </div>
  );
};

export default ProfessionalProfileConfig;